import { useEffect } from 'react';

import {
  DEFAULT_APPOINTMENT_PREVIEW_FIELDS,
  DEFAULT_CALENDAR_INTERVAL_HEIGHT_SIZE,
  DEFAULT_GRID_STEP_MINUTES,
  DEFAULT_MAX_VISIBLE_COLUMN_COUNT,
} from '@pages/Calendar/data';
import type {
  CalendarDisplaySettings,
  CalendarGridStepMinutes,
  CalendarIntervalHeightSize,
} from '@pages/Calendar/data';
import { normalizeAppointmentPreviewFields } from '@pages/Calendar/utils/normalizeAppointmentPreviewFields';

interface UseCalendarSettingsEffectsProps {
  storageKey: string | null;
  legacyStorageKey: string | null;
  setCalendarDisplaySettings(value: CalendarDisplaySettings): void;
  showNotification(error: unknown): void;
}

interface StoredCalendarSettings {
  maxVisibleColumnCount?: number;
  preferredMaxColumns?: number;
  intervalHeightSize?: CalendarIntervalHeightSize;
  gridStepMinutes?: CalendarGridStepMinutes;
  visibleMasterCount?: number;
  previewFields?: unknown;
}

const DEFAULT_CALENDAR_DISPLAY_SETTINGS: CalendarDisplaySettings = {
  intervalHeightSize: DEFAULT_CALENDAR_INTERVAL_HEIGHT_SIZE,
  preferredMaxColumns: DEFAULT_MAX_VISIBLE_COLUMN_COUNT,
  gridStepMinutes: DEFAULT_GRID_STEP_MINUTES,
  previewFields: [...DEFAULT_APPOINTMENT_PREVIEW_FIELDS],
};

const parseStoredSettings = (
  rawSettings: string | null
): StoredCalendarSettings => {
  if (!rawSettings) {
    return {};
  }

  return JSON.parse(rawSettings) as StoredCalendarSettings;
};

export const useCalendarSettingsEffects = ({
  storageKey,
  legacyStorageKey,
  setCalendarDisplaySettings,
  showNotification,
}: UseCalendarSettingsEffectsProps) => {
  useEffect(() => {
    if (!storageKey) {
      setCalendarDisplaySettings(DEFAULT_CALENDAR_DISPLAY_SETTINGS);
      return;
    }

    try {
      const rawSettings = window.localStorage.getItem(storageKey);
      const rawLegacySettings =
        rawSettings || !legacyStorageKey
          ? null
          : window.localStorage.getItem(legacyStorageKey);

      if (rawLegacySettings) {
        window.localStorage.setItem(storageKey, rawLegacySettings);
      }

      const storedSettings = parseStoredSettings(
        rawSettings ?? rawLegacySettings
      );

      setCalendarDisplaySettings({
        intervalHeightSize:
          storedSettings.intervalHeightSize ??
          DEFAULT_CALENDAR_DISPLAY_SETTINGS.intervalHeightSize,
        preferredMaxColumns:
          storedSettings.preferredMaxColumns ??
          storedSettings.maxVisibleColumnCount ??
          storedSettings.visibleMasterCount ??
          DEFAULT_CALENDAR_DISPLAY_SETTINGS.preferredMaxColumns,
        gridStepMinutes:
          storedSettings.gridStepMinutes ??
          DEFAULT_CALENDAR_DISPLAY_SETTINGS.gridStepMinutes,
        previewFields: normalizeAppointmentPreviewFields(
          storedSettings.previewFields
        ),
      });
    } catch (error) {
      showNotification(error);
      setCalendarDisplaySettings(DEFAULT_CALENDAR_DISPLAY_SETTINGS);
    }
  }, [
    legacyStorageKey,
    setCalendarDisplaySettings,
    showNotification,
    storageKey,
  ]);
};
